import { useContext, useEffect, useState } from "react";
import Button from "../atoms/button";
import { gradingTypes, gradingTypeSupportsAutograph } from "../utils/gradingTypes";
import ToastContext from "../../context/toastProvider";
import PackagePreview from "./PackagePreview";
import { FieldLabel } from "./FieldInfoTip";
import { PACKAGE_FIELD_HELP } from "./fieldHelp";
import { formatMoneyInput, handleMoneyInputChange } from "./moneyInput";
import { usePackageEditor } from "./usePackageEditor";

const fieldClass = "w-full rounded border border-primary/50 bg-black px-2 py-1 text-primary outline-none focus:border-primary";
const labelClass = "text-sm font-semibold text-primary";
const sectionClass = "rounded border border-primary/50 bg-mgs-bg p-4 text-left";
const stepBadgeClass = "inline-flex h-6 w-6 items-center justify-center rounded-full border border-primary bg-primary/20 text-xs font-bold text-primary";

const formatCents = (value) => new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
}).format(Number(value || 0) / 100);

const StepHeader = ({ step, title, hint }) => (
    <div className="mb-4">
        <div className="flex items-center gap-2">
            <span className={stepBadgeClass}>{step}</span>
            <h3 className="text-lg font-semibold text-primary">{title}</h3>
        </div>
        {hint ? <p className="mt-1 text-xs text-primary/65">{hint}</p> : null}
    </div>
);

const ReferencePanel = ({ quote, loading, savings, promoPriceCents }) => (
    <section className="rounded border border-dashed border-primary/40 bg-black/40 p-3">
        <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-primary">
                <FieldLabel help={PACKAGE_FIELD_HELP.referenceAnchor}>Âncora de preço</FieldLabel>
            </p>
            {loading ? <span className="text-xs text-primary/70">Atualizando...</span> : null}
        </div>
        {quote ? (
            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-primary md:grid-cols-2">
                <p>Preço cheio: <strong>{formatCents(quote.priceFullCents)}</strong></p>
                <p>Com desconto por qtd: <strong>{formatCents(quote.priceWithQtyDiscountCents)}</strong></p>
                <p>
                    Economia vs referência:{" "}
                    <strong>
                        {savings
                            ? `${formatCents(savings.savedCents)} (${savings.savedPercent}%)`
                            : "defina um preço promo menor"}
                    </strong>
                </p>
                <p className="text-xs text-primary/60 md:col-span-2">
                    Promo atual: {formatCents(promoPriceCents)}. Âncora usa tabela base (sem prioridade); o prazo do pacote é o que você definir.
                </p>
            </div>
        ) : (
            <p className="mt-2 text-sm text-primary/70">
                A âncora aparece ao montar as linhas de grading (tipo + quantidade).
            </p>
        )}
    </section>
);

const ToggleButton = ({ active, children, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`rounded border px-3 py-1 text-sm ${
            active
                ? "border-primary bg-primary text-black"
                : "border-primary/50 bg-black text-primary hover:border-primary"
        }`}
    >
        {children}
    </button>
);

const PackageEditor = ({ token, productPackage, onCancel, onSaved }) => {
    const { goodToast = () => null, badToast = () => null } = useContext(ToastContext);
    const [viewport, setViewport] = useState("desktop");
    const [surface, setSurface] = useState("vitrine");
    const [imagePreviewUrl, setImagePreviewUrl] = useState("");
    const editor = usePackageEditor({ token, productPackage });
    const {
        form,
        quote,
        savings,
        canGenerateCopy,
        imageFile,
        referenceLoading,
        generatingCopy,
        saving,
        uploadingImage,
        error,
        copyStatus,
        canAddItem,
        canRemoveItem,
        updateField,
        updateItem,
        addItem,
        removeItem,
        setImageFile,
        generateCopy,
        save,
    } = editor;
    const previewImageUrl = imagePreviewUrl || form.imageUrl;
    const referencePriceCents = quote?.selected?.priceFullCents ?? quote?.priceFullCents;

    useEffect(() => {
        if (!imageFile) {
            setImagePreviewUrl("");
            return undefined;
        }

        const objectUrl = URL.createObjectURL(imageFile);
        setImagePreviewUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [imageFile]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const result = await save();
        if (result?.type === "success") {
            onSaved?.(result);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
            <div className="space-y-4">
                <section className={sectionClass}>
                    <h2 className="text-xl font-semibold text-primary">
                        {productPackage ? "Editar pacote promocional" : "Novo pacote promocional"}
                    </h2>
                    <p className="mt-1 text-sm text-primary/70">
                        Fluxo: oferta → condições → textos → publicação. A IA só gera copy depois da oferta e do preço.
                    </p>
                </section>

                <section className={sectionClass}>
                    <StepHeader
                        step={1}
                        title="Oferta fechada"
                        hint="Monte uma ou mais linhas de grading (ex.: 1 Premium + 5 sem subgrades). O prazo fica na etapa seguinte."
                    />
                    <div className="space-y-3">
                        {(form.items || []).map((item, index) => (
                            <div
                                key={`item-${index}`}
                                className="grid grid-cols-1 gap-3 rounded border border-primary/30 bg-black/30 p-3 md:grid-cols-[minmax(0,1.4fr)_120px_auto_auto]"
                            >
                                <label className={labelClass}>
                                    <FieldLabel help={PACKAGE_FIELD_HELP.gradingType}>Tipo</FieldLabel>
                                    <select
                                        className={fieldClass}
                                        value={item.gradingType}
                                        onChange={(event) => updateItem(index, "gradingType", event.target.value)}
                                    >
                                        {Object.entries(gradingTypes).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className={labelClass}>
                                    <FieldLabel help={PACKAGE_FIELD_HELP.numberOfCards}>Quantidade</FieldLabel>
                                    <input
                                        className={fieldClass}
                                        type="number"
                                        min="1"
                                        value={item.numberOfCards}
                                        onChange={(event) => updateItem(index, "numberOfCards", event.target.value)}
                                    />
                                </label>
                                <label className={`${labelClass} flex items-center gap-2 md:pt-6`}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(item.autographEvaluation)}
                                        disabled={!gradingTypeSupportsAutograph(item.gradingType)}
                                        onChange={(event) => updateItem(index, "autographEvaluation", event.target.checked)}
                                    />
                                    <FieldLabel help={PACKAGE_FIELD_HELP.autograph}>Autógrafo</FieldLabel>
                                </label>
                                <div className="flex items-end">
                                    <Button
                                        type="button"
                                        classes="bg-transparent border border-primary/40 text-primary hover:border-primary"
                                        click={() => removeItem(index)}
                                        disabled={!canRemoveItem}
                                    >
                                        Remover
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <Button
                            type="button"
                            classes="bg-black border border-primary text-primary hover:bg-primary/10"
                            click={addItem}
                            disabled={!canAddItem}
                        >
                            + Adicionar tipo
                        </Button>
                    </div>
                </section>

                <section className={sectionClass}>
                    <StepHeader
                        step={2}
                        title="Condições da promoção"
                        hint="Preço e prazo fixos do pacote. Compare com a âncora antes de escrever a copy."
                    />
                    <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.promoPrice}>Preço promocional</FieldLabel>
                            <input
                                className={fieldClass}
                                type="text"
                                inputMode="numeric"
                                value={formatMoneyInput(form.promoPriceCents)}
                                onChange={(event) => handleMoneyInputChange(
                                    event.target.value,
                                    (cents) => updateField("promoPriceCents", cents)
                                )}
                            />
                        </label>
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.promoDeadline}>Prazo promocional (dias úteis)</FieldLabel>
                            <input
                                className={fieldClass}
                                type="number"
                                min="1"
                                value={form.promoDeadlineBusinessDays}
                                onChange={(event) => updateField("promoDeadlineBusinessDays", event.target.value)}
                            />
                        </label>
                    </div>
                    <ReferencePanel
                        quote={quote}
                        loading={referenceLoading}
                        savings={savings}
                        promoPriceCents={form.promoPriceCents}
                    />
                </section>

                <section className={sectionClass}>
                    <StepHeader
                        step={3}
                        title="Textos da oferta"
                        hint="Gere a copy só depois de fechar oferta e condições. Depois edite manualmente."
                    />
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs text-primary/65">
                            {canGenerateCopy
                                ? "Pronto para gerar: a IA usará produto, quantidade, preço, prazo e economia."
                                : "Bloqueado até preencher oferta + preço/prazo e carregar a referência."}
                        </p>
                        <Button
                            classes={canGenerateCopy ? "bg-primary hover:bg-primary/80" : "bg-primary/30 text-black/60"}
                            click={async () => {
                                const result = await generateCopy();
                                if (result?.type === "success") {
                                    goodToast("Copy gerada — revise título e subtítulo.");
                                } else if (result?.message) {
                                    badToast(result.message);
                                }
                            }}
                            disabled={!canGenerateCopy || generatingCopy}
                        >
                            {generatingCopy ? "Gerando..." : "Gerar copy com IA"}
                        </Button>
                    </div>
                    {copyStatus ? (
                        <p className="mb-3 rounded border border-emerald-500/40 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-200">
                            {copyStatus}
                        </p>
                    ) : null}
                    {error ? (
                        <p className="mb-3 rounded border border-red-500 bg-red-950/40 px-3 py-2 text-sm text-red-200">
                            {error}
                        </p>
                    ) : null}
                    <div className="grid grid-cols-1 gap-4">
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.title}>Título</FieldLabel>
                            <input
                                className={fieldClass}
                                value={form.title}
                                onChange={(event) => updateField("title", event.target.value)}
                                placeholder="Ex.: 10 Premium com economia real"
                            />
                        </label>
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.subtitle}>Subtítulo</FieldLabel>
                            <textarea
                                className={`${fieldClass} min-h-[80px]`}
                                value={form.subtitle}
                                onChange={(event) => updateField("subtitle", event.target.value)}
                                placeholder="Ex.: Pacote fechado com prazo promocional e desconto vs tabela."
                            />
                        </label>
                    </div>
                </section>

                <section className={sectionClass}>
                    <StepHeader
                        step={4}
                        title="Identidade e publicação"
                        hint="Nome interno/slug para o admin; vigência e status para liberar a oferta."
                    />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.internalName}>Nome interno</FieldLabel>
                            <input
                                className={fieldClass}
                                value={form.internalName}
                                onChange={(event) => updateField("internalName", event.target.value)}
                                placeholder="Pacote Premium 10 cartas"
                            />
                        </label>
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.slug}>Slug</FieldLabel>
                            <input
                                className={fieldClass}
                                value={form.slug}
                                onChange={(event) => updateField("slug", event.target.value)}
                                placeholder="pacote-premium-10"
                            />
                        </label>
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.startsAt}>Início</FieldLabel>
                            <input
                                className={fieldClass}
                                type="date"
                                value={form.startsAt}
                                onChange={(event) => updateField("startsAt", event.target.value)}
                            />
                        </label>
                        <label className={labelClass}>
                            <FieldLabel help={PACKAGE_FIELD_HELP.endsAt}>Fim</FieldLabel>
                            <input
                                className={fieldClass}
                                type="date"
                                value={form.endsAt}
                                onChange={(event) => updateField("endsAt", event.target.value)}
                            />
                        </label>
                        <label className={`${labelClass} flex items-center gap-2`}>
                            <input
                                type="checkbox"
                                checked={form.isActive}
                                onChange={(event) => updateField("isActive", event.target.checked)}
                            />
                            <FieldLabel help={PACKAGE_FIELD_HELP.isActive}>Ativo na vigência</FieldLabel>
                        </label>
                    </div>
                </section>

                <section className={sectionClass}>
                    <StepHeader
                        step={5}
                        title="Visual (opcional)"
                        hint="Sem imagem, o preview usa tipografia, quantidade, economia e preço como hero."
                    />
                    <p className={`${labelClass} mb-2`}>
                        <FieldLabel help={PACKAGE_FIELD_HELP.image}>Imagem do pacote</FieldLabel>
                    </p>
                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="text-sm text-primary"
                        onChange={(event) => setImageFile(event.target.files?.[0] || null)}
                    />
                    <p className="mt-2 text-xs text-primary/70">
                        {imageFile ? `Selecionado: ${imageFile.name}` : "PNG, JPG ou WebP até 5MB."}
                    </p>
                </section>

                {error ? <p className="rounded border border-red-500 bg-red-950/40 p-3 text-sm text-red-200">{error}</p> : null}

                <div className="flex flex-wrap gap-3">
                    <Button classes="bg-red-600 text-white hover:bg-red-500" click={onCancel} disabled={saving || uploadingImage}>
                        Cancelar
                    </Button>
                    <Button type="submit" classes="bg-primary hover:bg-primary/80" disabled={saving || uploadingImage}>
                        {saving || uploadingImage ? "Salvando..." : "Salvar pacote"}
                    </Button>
                </div>
            </div>

            <aside className="h-fit rounded border border-primary bg-black p-4 lg:sticky lg:top-4">
                <p className="mb-3 text-sm font-semibold text-primary">Preview ao vivo</p>
                <div className="mb-4 flex flex-wrap gap-2">
                    <ToggleButton active={viewport === "desktop"} onClick={() => setViewport("desktop")}>Desktop</ToggleButton>
                    <ToggleButton active={viewport === "mobile"} onClick={() => setViewport("mobile")}>Mobile</ToggleButton>
                    <ToggleButton active={surface === "vitrine"} onClick={() => setSurface("vitrine")}>Vitrine</ToggleButton>
                    <ToggleButton active={surface === "carrinho"} onClick={() => setSurface("carrinho")}>Carrinho</ToggleButton>
                </div>
                <PackagePreview
                    title={form.title}
                    subtitle={form.subtitle}
                    items={form.items}
                    promoPriceCents={form.promoPriceCents}
                    promoDeadlineBusinessDays={form.promoDeadlineBusinessDays}
                    imageUrl={previewImageUrl}
                    referencePriceCents={referencePriceCents}
                    savingsCents={savings?.savedCents}
                    savingsPercent={savings?.savedPercent}
                    viewport={viewport}
                    surface={surface}
                />
            </aside>
        </form>
    );
};

export default PackageEditor;
